import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import BasemapTiles from "./BasemapTiles";
import MapSizeObserver from "./MapSizeObserver";
import "leaflet/dist/leaflet.css";

const DEFAULT_CENTER = [32.7767, -96.797];
const DEFAULT_ZOOM = 10;
const MAP_VIEW_STORAGE_KEY = "avl-map-view";

const saveViewPreference = (center, zoom) => {
  if (typeof window === "undefined") {
    return;
  }

  const payload = {
    center: [Number(center.lat.toFixed(6)), Number(center.lng.toFixed(6))],
    zoom,
  };

  window.localStorage.setItem(MAP_VIEW_STORAGE_KEY, JSON.stringify(payload));
};

const loadViewPreference = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(MAP_VIEW_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (
      !Array.isArray(parsed.center) ||
      parsed.center.length !== 2 ||
      typeof parsed.zoom !== "number"
    ) {
      return null;
    }

    return {
      center: parsed.center,
      zoom: parsed.zoom,
    };
  } catch (error) {
    console.warn("Failed to parse AVL map view preference", error);
    return null;
  }
};

const clearViewPreference = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(MAP_VIEW_STORAGE_KEY);
};

function ResetViewControl({ onReset }) {
  useMap();

  return (
    <div className="leaflet-top leaflet-right" style={{ pointerEvents: "none" }}>
      <div className="leaflet-control" style={{ background: "rgba(17, 24, 39, 0.75)", padding: 0, border: "none", boxShadow: "0 4px 10px rgba(0,0,0,0.25)", borderRadius: 8 }}>
        <button
          type="button"
          onClick={onReset}
          style={{
            pointerEvents: "auto",
            border: "none",
            background: "transparent",
            color: "#f9fafb",
            fontWeight: 600,
            padding: "8px 14px",
            cursor: "pointer",
            fontSize: "0.85rem",
          }}
        >
          Reset View
        </button>
      </div>
    </div>
  );
}

// Follow user location component with manual interaction pause
const FOLLOW_PAUSE_DURATION_MS = 60000; // 60 seconds

function FollowUserLocation({ position, followEnabled, onManualInteraction }) {
  const map = useMap();
  const lastFollowedPosition = useRef(null);
  const isProgrammaticMove = useRef(false);

  // Detect manual map interactions
  useMapEvents({
    dragstart: () => {
      if (!isProgrammaticMove.current) {
        onManualInteraction?.();
      }
    },
  });

  useEffect(() => {
    if (!position || !followEnabled) {
      return;
    }

    // Only update if position actually changed (avoid jitter)
    const posKey = `${position[0].toFixed(5)},${position[1].toFixed(5)}`;
    if (lastFollowedPosition.current === posKey) {
      return;
    }
    lastFollowedPosition.current = posKey;

    // Mark as programmatic to avoid triggering onManualInteraction
    isProgrammaticMove.current = true;
    map.setView(position, map.getZoom(), { animate: true });
    
    // Reset flag after animation completes
    setTimeout(() => {
      isProgrammaticMove.current = false;
    }, 300);
  }, [map, position, followEnabled]);

  return null;
}

const ensureMarkerAnimations = () => {
  if (typeof document === "undefined") {
    return;
  }
  if (document.getElementById("avl-marker-animations")) {
    return;
  }
  const style = document.createElement("style");
  style.id = "avl-marker-animations";
  style.textContent = `
    @keyframes avl-flash-red {
      0%, 100% { opacity: 0.45; transform: scale(0.92); }
      50% { opacity: 1; transform: scale(1.1); }
    }
    @keyframes avl-flash-yellow {
      0%, 100% { opacity: 0.45; transform: scale(0.92); }
      50% { opacity: 1; transform: scale(1.05); }
    }
    @keyframes avl-flash-incident {
      0%, 100% { opacity: 0.7; transform: scale(0.95); }
      50% { opacity: 1; transform: scale(1.08); }
    }
    @keyframes avl-flash-blue {
      0%, 100% { opacity: 0.5; transform: scale(0.94); }
      50% { opacity: 1; transform: scale(1.08); }
    }
  `;
  document.head.appendChild(style);
};

const resolveMarkerAppearance = (status, forceStatusCheck = false, isStale = false) => {
  // Stale units are always gray with no animation
  if (isStale) {
    return { color: "#9ca3af", animation: null }; // Gray color for stale
  }

  if (forceStatusCheck) {
    return { color: "#fbbf24", animation: "avl-flash-yellow 1.2s ease-in-out infinite" };
  }

  const normalized = (status || "").toLowerCase();

  if (normalized.includes("emergency")) {
    return { color: "#dc2626", animation: "avl-flash-red 0.9s ease-in-out infinite" };
  }

  if (normalized.includes("check status")) {
    return { color: "#fbbf24", animation: "avl-flash-yellow 1.2s ease-in-out infinite" };
  }

  if (normalized.includes("enroute") || normalized.includes("en route")) {
    return { color: "#2563eb", animation: "avl-flash-blue 1.2s ease-in-out infinite" };
  }

  if (normalized.includes("busy")) {
    return { color: "#f97316" };
  }

  if (normalized.includes("out")) {
    return { color: "#6b7280" };
  }

  if (normalized.includes("scene")) {
    return { color: "#2563eb" };
  }

  return { color: "#16a34a" };
};

const createMarkerIcon = (status, isDarkMode, forceStatusCheck = false, label = "", isStale = false, showStatusText = false) => {
  ensureMarkerAnimations();
  const { color, animation } = resolveMarkerAppearance(status, forceStatusCheck, isStale);
  const borderColor = isDarkMode ? "rgba(17, 17, 17, 0.9)" : "rgba(255, 255, 255, 0.95)";
  const textColor = color;
  const labelText = (label || "").toUpperCase();
  const textShadow = isDarkMode
    ? "0 0 6px rgba(0,0,0,0.8), 0 0 2px rgba(0,0,0,0.8)"
    : "0 0 4px rgba(255,255,255,0.9), 0 0 2px rgba(255,255,255,0.7)";
  const opacity = isStale ? "0.6" : "1";
  
  // Determine status badge text
  let statusBadge = "";
  const normalizedStatus = (status || "").toLowerCase();
  if (normalizedStatus.includes("scene")) {
    statusBadge = "ON SCENE";
  } else if (normalizedStatus.includes("busy")) {
    statusBadge = "BUSY";
  } else if (normalizedStatus.includes("out") && !normalizedStatus.includes("enroute")) {
    statusBadge = "OUT";
  } else if (normalizedStatus.includes("enroute") || normalizedStatus.includes("en route")) {
    statusBadge = "ENROUTE";
  }
  
  const statusBadgeHtml = showStatusText && statusBadge ? `
    <div style="
      font-size: 0.7rem;
      font-weight: 800;
      letter-spacing: 0.5px;
      color: ${color};
      text-transform: uppercase;
      text-shadow: ${textShadow};
      background: ${isDarkMode ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.85)'};
      padding: 2px 6px;
      border-radius: 4px;
      margin-top: 2px;
      white-space: nowrap;
    ">${statusBadge}</div>
  ` : '';
  
  const html = `
    <div style="display:flex;flex-direction:column;align-items:center;gap:4px;opacity:${opacity};">
      <div style="
        font-size: 0.95rem;
        font-weight: 800;
        letter-spacing: 0.6px;
        color: ${textColor};
        text-transform: uppercase;
        text-shadow: ${textShadow};
      ">${labelText}</div>
      <div style="
        width: 30px;
        height: 30px;
        border-radius: 50%;
        background: ${color};
        border: 3px solid ${borderColor};
        box-shadow: 0 8px 18px rgba(0,0,0,0.45);
        animation: ${animation || "none"};
      "></div>
      ${statusBadgeHtml}
    </div>
  `;

  return L.divIcon({
    className: "avl-status-marker",
    html,
    iconSize: [40, showStatusText && statusBadge ? 70 : 50],
    iconAnchor: [20, showStatusText && statusBadge ? 50 : 40],
    popupAnchor: [0, showStatusText && statusBadge ? -40 : -30],
  });
};

// Create an incident marker icon (triangle/diamond shape for incidents)
const createIncidentIcon = (incidentType, isDarkMode, source = "") => {
  ensureMarkerAnimations();
  
  // Color based on incident type
  let color = "#ef4444"; // Default red
  if (incidentType?.toLowerCase().includes("assist")) {
    color = "#f97316"; // Orange for assist motorist
  } else if (incidentType?.toLowerCase().includes("debris")) {
    color = "#eab308"; // Yellow for debris
  } else if (incidentType?.toLowerCase().includes("accident")) {
    color = "#dc2626"; // Red for accidents
  }
  
  const isWaze = source?.toLowerCase() === "waze";
  const borderColor = isDarkMode ? "rgba(17, 17, 17, 0.9)" : "rgba(255, 255, 255, 0.95)";
  const textShadow = isDarkMode
    ? "0 0 6px rgba(0,0,0,0.8), 0 0 2px rgba(0,0,0,0.8)"
    : "0 0 4px rgba(255,255,255,0.9), 0 0 2px rgba(255,255,255,0.7)";
  
  // Diamond/triangle marker for incidents
  const html = `
    <div style="display:flex;flex-direction:column;align-items:center;gap:2px;">
      ${isWaze ? `<div style="
        font-size: 0.65rem;
        font-weight: 800;
        letter-spacing: 0.5px;
        color: #06b6d4;
        text-transform: uppercase;
        text-shadow: ${textShadow};
        background: rgba(0,0,0,0.5);
        padding: 1px 4px;
        border-radius: 3px;
      ">WAZE</div>` : ""}
      <div style="
        width: 28px;
        height: 28px;
        background: ${color};
        border: 3px solid ${borderColor};
        box-shadow: 0 6px 14px rgba(0,0,0,0.5);
        transform: rotate(45deg);
        animation: avl-flash-incident 1.5s ease-in-out infinite;
      "></div>
    </div>
  `;

  return L.divIcon({
    className: "avl-incident-marker",
    html,
    iconSize: [36, 50],
    iconAnchor: [18, 45],
    popupAnchor: [0, -35],
  });
};

// Create a destination marker (where the user is heading - prominent pulsing marker)
const createDestinationIcon = (isDarkMode, incidentType = "") => {
  ensureMarkerAnimations();
  
  const borderColor = isDarkMode ? "rgba(17, 17, 17, 0.9)" : "rgba(255, 255, 255, 0.95)";
  const textShadow = isDarkMode
    ? "0 0 6px rgba(0,0,0,0.8), 0 0 2px rgba(0,0,0,0.8)"
    : "0 0 4px rgba(255,255,255,0.9), 0 0 2px rgba(255,255,255,0.7)";
  
  const html = `
    <div style="display:flex;flex-direction:column;align-items:center;gap:4px;">
      <div style="
        font-size: 0.7rem;
        font-weight: 800;
        letter-spacing: 0.5px;
        color: #22c55e;
        text-transform: uppercase;
        text-shadow: ${textShadow};
        background: rgba(0,0,0,0.6);
        padding: 2px 6px;
        border-radius: 4px;
      ">DESTINATION</div>
      <div style="
        width: 36px;
        height: 36px;
        background: #22c55e;
        border: 4px solid ${borderColor};
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        box-shadow: 0 8px 20px rgba(34, 197, 94, 0.6);
        animation: avl-flash-blue 1s ease-in-out infinite;
      "></div>
    </div>
  `;

  return L.divIcon({
    className: "avl-destination-marker",
    html,
    iconSize: [44, 60],
    iconAnchor: [22, 55],
    popupAnchor: [0, -45],
  });
};

const createAbandonedVehicleIcon = (isDarkMode) => {
  ensureMarkerAnimations();
  const color = "#f59e0b";
  const borderColor = isDarkMode ? "rgba(17, 17, 17, 0.9)" : "rgba(255, 255, 255, 0.95)";
  const textShadow = isDarkMode
    ? "0 0 6px rgba(0,0,0,0.8), 0 0 2px rgba(0,0,0,0.8)"
    : "0 0 4px rgba(255,255,255,0.9), 0 0 2px rgba(255,255,255,0.7)";

  const html = `
    <div style="display:flex;flex-direction:column;align-items:center;gap:2px;">
      <div style="
        font-size: 0.65rem;
        font-weight: 800;
        letter-spacing: 0.6px;
        color: ${color};
        text-transform: uppercase;
        text-shadow: ${textShadow};
        background: rgba(0,0,0,0.55);
        padding: 1px 4px;
        border-radius: 3px;
      ">ABANDONED</div>
      <div style="
        width: 24px;
        height: 24px;
        border-radius: 6px;
        background: ${color};
        border: 3px solid ${borderColor};
        box-shadow: 0 6px 14px rgba(0,0,0,0.45);
        animation: avl-flash-incident 1.6s ease-in-out infinite;
      "></div>
    </div>
  `;

  return L.divIcon({
    className: "avl-abandoned-marker",
    html,
    iconSize: [34, 46],
    iconAnchor: [17, 40],
    popupAnchor: [0, -30],
  });
};

export function MapViewPersistenceWithReset({ coordinates, resetToken }) {
  const map = useMap();
  const initialized = useRef(false);
  const hasUserOverride = useRef(false);
  const suppressEvents = useRef(false);
  const movementStarted = useRef(false);
  const lastCoordinatesLength = useRef(0);

  const applyView = useCallback((callback) => {
    movementStarted.current = false;
    suppressEvents.current = true;
    callback();
    setTimeout(() => {
      suppressEvents.current = false;
    }, 0);
  }, []);

  const persistCurrentView = useCallback(() => {
    const center = map.getCenter();
    saveViewPreference(center, map.getZoom());
  }, [map]);

  useEffect(() => {
    if (initialized.current) {
      return;
    }

    initialized.current = true;
    const storedView = loadViewPreference();

    if (storedView) {
      applyView(() => map.setView(storedView.center, storedView.zoom));
      hasUserOverride.current = true;
      return;
    }

    if (!coordinates || coordinates.length === 0) {
      applyView(() => map.setView(DEFAULT_CENTER, DEFAULT_ZOOM));
    }
  }, [applyView, coordinates, map]);

  // Only auto-fit bounds when coordinates go from 0 to some, not on every update
  useEffect(() => {
    // If user has overridden view, never auto-fit
    if (hasUserOverride.current) {
      lastCoordinatesLength.current = coordinates?.length || 0;
      return;
    }

    // If no coordinates, nothing to do
    if (!coordinates || coordinates.length === 0) {
      lastCoordinatesLength.current = 0;
      return;
    }

    // Only auto-fit on initial load (when going from 0 to some coordinates)
    // After that, let user control the view
    if (lastCoordinatesLength.current === 0 && coordinates.length > 0) {
      if (coordinates.length === 1) {
        applyView(() => map.setView(coordinates[0], Math.max(map.getZoom(), DEFAULT_ZOOM)));
      } else {
        applyView(() => map.fitBounds(coordinates, { padding: [32, 32] }));
      }
    }
    
    lastCoordinatesLength.current = coordinates.length;
  }, [applyView, coordinates, map]);

  useEffect(() => {
    const handleMoveStart = () => {
      if (suppressEvents.current) return;
      movementStarted.current = true;
      hasUserOverride.current = true;
    };
    const handleViewChange = () => {
      // Leaflet invalidation emits moveend without movestart. A layout resize
      // must not become a saved user override or suppress initial data fitting.
      if (suppressEvents.current || !movementStarted.current) return;
      movementStarted.current = false;
      persistCurrentView();
    };

    map.on("movestart", handleMoveStart);
    map.on("zoomend", handleViewChange);
    map.on("moveend", handleViewChange);

    return () => {
      movementStarted.current = false;
      map.off("movestart", handleMoveStart);
      map.off("zoomend", handleViewChange);
      map.off("moveend", handleViewChange);
    };
  }, [map, persistCurrentView]);

  useEffect(() => {
    if (!resetToken) {
      return;
    }

    clearViewPreference();
    hasUserOverride.current = false;

    if (!coordinates || coordinates.length === 0) {
      applyView(() => map.setView(DEFAULT_CENTER, DEFAULT_ZOOM));
      return;
    }

    if (coordinates.length === 1) {
      applyView(() => map.setView(coordinates[0], Math.max(DEFAULT_ZOOM, map.getZoom())));
    } else {
      applyView(() => map.fitBounds(coordinates, { padding: [32, 32] }));
    }
  }, [applyView, coordinates, map, resetToken]);

  return null;
}

// Create Waze alert marker icon with category-based colors and label
const createWazeAlertIcon = (category, isDarkMode, mapLabel, enlarged = false) => {
  // Color mapping for Waze alert categories
  const categoryColors = {
    ASSIST_MOTORIST: "#166534",      // Dark green
    DEBRIS: "#c2410c",               // Dark orange  
    ACCIDENT: "#b91c1c",             // Dark red
    ASSIST_OTHER_AGENCY: "#1e40af", // Dark blue
  };
  
  const color = categoryColors[category] || "#6b7280"; // Default gray
  const borderColor = isDarkMode ? "rgba(17, 17, 17, 0.9)" : "rgba(255, 255, 255, 0.95)";
  const textColor = isDarkMode ? "#f9fafb" : "#111827";
  const textBg = isDarkMode ? "rgba(17, 24, 39, 0.85)" : "rgba(255, 255, 255, 0.9)";
  
  // Enlarged sizes for map-centered/driving view
  const dotSize = enlarged ? 22 : 14;
  const fontSize = enlarged ? 14 : 10;
  const padding = enlarged ? "4px 8px" : "2px 5px";
  const borderWidth = enlarged ? 3 : 2;
  const marginLeft = enlarged ? 6 : 4;
  
  const html = `
    <div style="display:flex;flex-direction:row;align-items:center;white-space:nowrap;">
      <div style="
        width: ${dotSize}px;
        height: ${dotSize}px;
        border-radius: 50%;
        background: ${color};
        border: ${borderWidth}px solid ${borderColor};
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        flex-shrink: 0;
      "></div>
      <div style="
        margin-left: ${marginLeft}px;
        font-size: ${fontSize}px;
        font-weight: 700;
        color: ${textColor};
        background: ${textBg};
        padding: ${padding};
        border-radius: ${enlarged ? 4 : 3}px;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        box-shadow: 0 1px 2px rgba(0,0,0,0.2);
      ">${mapLabel || ""}</div>
    </div>
  `;
  
  // Calculate icon width based on label length
  const labelWidth = mapLabel ? Math.max(enlarged ? 120 : 80, mapLabel.length * (enlarged ? 10 : 7) + (enlarged ? 50 : 30)) : (enlarged ? 28 : 18);
  const iconHeight = enlarged ? 28 : 18;
  
  return L.divIcon({
    className: "avl-waze-marker",
    html,
    iconSize: [labelWidth, iconHeight],
    iconAnchor: [enlarged ? 13 : 9, enlarged ? 13 : 9],
    popupAnchor: [0, enlarged ? -16 : -12],
  });
};

export default function AvlMap({ 
  units, 
  incidents = [], 
  wazeAlerts = [], 
  abandonedVehicles = [],
  destination = null, 
  enrouteUnitId = null, 
  tabletMode, 
  isDarkMode = false, 
  onIncidentClick, 
  weatherTileUrl = null, 
  readOnly = false, 
  height = null,
  // Follow mode props
  userPosition = null,
  followEnabled = false,
  onManualInteraction = null,
  // Map-centered/driving mode - enlarged markers
  enlargedMarkers = false,
}) {
  const sanitizedUnits = useMemo(
    () =>
      units.filter(
        (unit) => Number.isFinite(unit?.latitude) && Number.isFinite(unit?.longitude)
      ),
    [units]
  );
  
  const sanitizedIncidents = useMemo(
    () =>
      incidents.filter(
        (incident) => Number.isFinite(incident?.latitude) && Number.isFinite(incident?.longitude)
      ),
    [incidents]
  );

  const sanitizedWazeAlerts = useMemo(
    () =>
      wazeAlerts.filter(
        (alert) => Number.isFinite(alert?.latitude) && Number.isFinite(alert?.longitude)
      ),
    [wazeAlerts]
  );

  const sanitizedAbandonedVehicles = useMemo(
    () =>
      abandonedVehicles.filter(
        (vehicle) => Number.isFinite(vehicle?.latitude) && Number.isFinite(vehicle?.longitude)
      ),
    [abandonedVehicles]
  );
  
  const unitCoordinates = useMemo(
    () => sanitizedUnits.map((unit) => [unit.latitude, unit.longitude]),
    [sanitizedUnits]
  );
  
  const incidentCoordinates = useMemo(
    () => sanitizedIncidents.map((incident) => [incident.latitude, incident.longitude]),
    [sanitizedIncidents]
  );

  const wazeAlertCoordinates = useMemo(
    () => sanitizedWazeAlerts.map((alert) => [alert.latitude, alert.longitude]),
    [sanitizedWazeAlerts]
  );

  const abandonedVehicleCoordinates = useMemo(
    () => sanitizedAbandonedVehicles.map((vehicle) => [vehicle.latitude, vehicle.longitude]),
    [sanitizedAbandonedVehicles]
  );

  const destinationCoordinates = useMemo(() => {
    if (destination && Number.isFinite(destination.latitude) && Number.isFinite(destination.longitude)) {
      return [[destination.latitude, destination.longitude]];
    }
    return [];
  }, [destination]);

  // Calculate route line from enroute unit to destination
  const routeLine = useMemo(() => {
    if (!enrouteUnitId || !destination) {
      return null;
    }
    const unit = sanitizedUnits.find(
      (u) => u.id?.toUpperCase() === enrouteUnitId?.toUpperCase()
    );
    if (!unit || !Number.isFinite(unit.latitude) || !Number.isFinite(unit.longitude)) {
      return null;
    }
    if (!Number.isFinite(destination.latitude) || !Number.isFinite(destination.longitude)) {
      return null;
    }
    return [
      [unit.latitude, unit.longitude],
      [destination.latitude, destination.longitude],
    ];
  }, [enrouteUnitId, destination, sanitizedUnits]);
  
  const coordinates = useMemo(
    () => [...unitCoordinates, ...incidentCoordinates, ...wazeAlertCoordinates, ...abandonedVehicleCoordinates, ...destinationCoordinates],
    [unitCoordinates, incidentCoordinates, wazeAlertCoordinates, abandonedVehicleCoordinates, destinationCoordinates]
  );
  
  const center = coordinates[0] || DEFAULT_CENTER;
  const [resetToken, setResetToken] = useState(0);

  const requestReset = useCallback(() => {
    setResetToken((token) => token + 1);
  }, []);

  // Compute map height
  const mapHeight = height || (tabletMode ? 460 : 380);

  return (
    <div style={{ position: "relative", height: typeof height === "string" ? height : undefined }}>
      <MapContainer
        center={center}
        zoom={DEFAULT_ZOOM}
        scrollWheelZoom
        style={{ height: typeof mapHeight === "number" ? mapHeight : "100%", width: "100%", borderRadius: 12, overflow: "hidden" }}
      >
        <BasemapTiles dark={isDarkMode} />
        {/* Weather Overlay Layer */}
        {weatherTileUrl && (
          <TileLayer 
            key="weather-layer"
            url={weatherTileUrl} 
            opacity={0.6}
            zIndex={100}
          />
        )}
        <MapSizeObserver />
        <MapViewPersistenceWithReset coordinates={coordinates} resetToken={resetToken} />
        <ResetViewControl onReset={requestReset} />
        
        {/* Follow user location when enabled */}
        <FollowUserLocation 
          position={userPosition} 
          followEnabled={followEnabled} 
          onManualInteraction={onManualInteraction}
        />

        {/* Render Waze alert markers (below incidents and units) */}
        {sanitizedWazeAlerts.map((alert) => {
          const categoryLabels = {
            ASSIST_MOTORIST: "Assist Motorist",
            DEBRIS: "Debris",
            ACCIDENT: "Accident",
            ASSIST_OTHER_AGENCY: "Assist Other Agency",
          };
          const categoryColors = {
            ASSIST_MOTORIST: "#166534",
            DEBRIS: "#c2410c",
            ACCIDENT: "#b91c1c",
            ASSIST_OTHER_AGENCY: "#1e40af",
          };
          return (
            <Marker
              key={alert.id}
              position={[alert.latitude, alert.longitude]}
              icon={createWazeAlertIcon(alert.category, isDarkMode, alert.mapLabel, enlargedMarkers)}
            >
              <Popup>
                <strong style={{ color: categoryColors[alert.category] || "#6b7280" }}>
                  WAZE: {categoryLabels[alert.category] || "Unknown"}
                </strong>
                <div><strong>Location:</strong> {[alert.street, alert.city].filter(Boolean).join(", ") || "N/A"}</div>
                {alert.distanceMiles !== null && (
                  <div><strong>Distance:</strong> {alert.distanceMiles.toFixed(2)} mi</div>
                )}
                <div><strong>Status:</strong> {alert.status?.toUpperCase() || "ACTIVE"}</div>
              </Popup>
            </Marker>
          );
        })}

        {/* Render abandoned vehicle markers (below incidents) */}
        {sanitizedAbandonedVehicles.map((vehicle) => (
          <Marker
            key={`abandoned-${vehicle.id}`}
            position={[vehicle.latitude, vehicle.longitude]}
            icon={createAbandonedVehicleIcon(isDarkMode)}
          >
            <Popup>
              <strong style={{ color: "#f59e0b" }}>ABANDONED VEHICLE</strong>
              <div><strong>Location:</strong> {vehicle.locationDescription || "N/A"}</div>
              {vehicle.shoulderLocation && (
                <div><strong>Shoulder:</strong> {vehicle.shoulderLocation}</div>
              )}
              <div>
                <strong>Vehicle:</strong>{" "}
                {[vehicle.vehicleYear, vehicle.vehicleMake, vehicle.vehicleModel, vehicle.vehicleColor]
                  .filter(Boolean)
                  .join(" ") || "N/A"}
              </div>
              {(vehicle.vehiclePlate || vehicle.vehicleState) && (
                <div><strong>Plate:</strong> {[vehicle.vehiclePlate, vehicle.vehicleState].filter(Boolean).join(" ")}</div>
              )}
              {vehicle.notes && <div><strong>Notes:</strong> {vehicle.notes}</div>}
            </Popup>
          </Marker>
        ))}

        {/* Render route line from enroute unit to destination */}
        {routeLine && (
          <Polyline
            positions={routeLine}
            pathOptions={{
              color: "#2563eb",
              weight: 4,
              opacity: 0.8,
              dashArray: "10, 10",
              lineCap: "round",
            }}
          />
        )}
        {/* Render incident markers first (below unit markers) - skip the destination incident */}
        {sanitizedIncidents
          .filter((incident) => !destination || incident.id !== destination.id)
          .map((incident) => (
          <Marker
            key={`incident-${incident.id}`}
            position={[incident.latitude, incident.longitude]}
            icon={createIncidentIcon(incident.incidentType, isDarkMode, incident.source)}
            eventHandlers={{
              click: () => onIncidentClick?.(incident.id),
            }}
          >
            <Popup>
              <strong style={{ color: "#ef4444" }}>{incident.cadId || `INC-${incident.id}`}</strong>
              <div><strong>Type:</strong> {incident.incidentType}</div>
              <div><strong>Location:</strong> {incident.locationDescription || "N/A"}</div>
              <div><strong>Primary:</strong> {incident.primaryRangerId || "Unassigned"}</div>
              {incident.source && <div><strong>Source:</strong> {incident.source}</div>}
            </Popup>
          </Marker>
        ))}
        {/* Render unit markers on top */}
        {sanitizedUnits.map((unit) => (
          <Marker
            key={unit.id}
            position={[unit.latitude, unit.longitude]}
            icon={createMarkerIcon(unit.status, isDarkMode, unit.needsStatusCheck, unit.id, unit.isStale, enlargedMarkers)}
          >
            <Popup>
              <strong>{unit.id}</strong>
              {unit.isStale && <div style={{ color: "#9ca3af", fontWeight: "bold" }}>STALE - Last known location</div>}
              <div>Status: {unit.isStale ? "STALE" : unit.status}</div>
              <div>Info: {unit.isStale ? "—" : (unit.info || "N/A")}</div>
              <div>Division: {unit.division || "-"}</div>
              <div>Location: {unit.locationLabel || unit.location || "NOT PROVIDED"}</div>
              <div>Updated: {unit.updatedAt || "--:--"}{unit.isStale ? " (STALE)" : ""}</div>
              <div>GPS fix: {unit.gpsObservedAt ? new Date(unit.gpsObservedAt).toLocaleString() : "Time unavailable"}{Number.isFinite(unit.accuracyMeters) ? ` (accuracy ${Math.round(unit.accuracyMeters)} m)` : ''}</div>
            </Popup>
          </Marker>
        ))}
        {/* Render destination marker (where user is heading) */}
        {destination && Number.isFinite(destination.latitude) && Number.isFinite(destination.longitude) && (
          <Marker
            key="destination"
            position={[destination.latitude, destination.longitude]}
            icon={createDestinationIcon(isDarkMode, destination.incidentType)}
            eventHandlers={{
              click: () => destination.id && onIncidentClick?.(destination.id),
            }}
          >
            <Popup>
              <strong style={{ color: "#22c55e" }}>DESTINATION</strong>
              <div><strong>CAD:</strong> {destination.cadId || "N/A"}</div>
              <div><strong>Type:</strong> {destination.incidentType || "N/A"}</div>
              <div><strong>Location:</strong> {destination.locationDescription || "N/A"}</div>
              {destination.source && <div><strong>Source:</strong> {destination.source}</div>}
            </Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
  );
}
